import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, ArrowLeft, Upload, Link as LinkIcon, Sparkles } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import axios from 'axios';
import AppHeader from '@/components/AppHeader';

// Use current origin for API calls
const API = `${window.location.origin}/api`;

const BriefingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forCustomerEmail = searchParams.get('forCustomer'); // Admin creating for customer
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: '',
    business_description: '',
    target_audience: '',
    competitor_urls: [''],
    font_preferences: '',
    content_files: [],
    logo_file: null,
    favicon_file: null
  });

  // Handle file drops for content
  const onDropContent = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          content_files: [...prev.content_files, { filename: file.name, base64_data: base64 }]
        }));
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Handle file drop for logo
  const onDropLogo = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          logo_file: { filename: file.name, base64_data: base64 }
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Handle file drop for favicon
  const onDropFavicon = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          favicon_file: { filename: file.name, base64_data: base64 }
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps: getContentRootProps, getInputProps: getContentInputProps, isDragActive: isContentDragActive } = useDropzone({
    onDrop: onDropContent,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: true
  });

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps, isDragActive: isLogoDragActive } = useDropzone({
    onDrop: onDropLogo,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg']
    },
    multiple: false,
    maxSize: 5242880 // 5MB
  });

  const { getRootProps: getFaviconRootProps, getInputProps: getFaviconInputProps, isDragActive: isFaviconDragActive } = useDropzone({
    onDrop: onDropFavicon,
    accept: {
      'image/*': ['.png', '.ico', '.svg']
    },
    multiple: false,
    maxSize: 1048576 // 1MB
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCompetitorUrlChange = (index, value) => {
    const newUrls = [...formData.competitor_urls];
    newUrls[index] = value;
    setFormData(prev => ({ ...prev, competitor_urls: newUrls }));
  };

  const addCompetitorUrl = () => {
    setFormData(prev => ({ ...prev, competitor_urls: [...prev.competitor_urls, ''] }));
  };

  const removeContentFile = (index) => {
    setFormData(prev => ({
      ...prev,
      content_files: prev.content_files.filter((_, i) => i !== index)
    }));
  };

  const nextStep = () => {
    // Validation
    if (currentStep === 1) {
      if (!formData.business_name || !formData.business_type) {
        toast.error('Please fill in business name and type');
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!formData.business_description) {
        toast.error('Please provide a business description');
        return;
      }
    }

    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const payload = {
        business_info: {
          business_name: formData.business_name,
          business_type: formData.business_type,
          business_description: formData.business_description,
          target_audience: formData.target_audience,
          competitor_urls: formData.competitor_urls.filter(url => url.trim()),
          font_preferences: formData.font_preferences
        },
        content_files: formData.content_files,
        logo_file: formData.logo_file,
        favicon_file: formData.favicon_file
      };

      // If admin is creating for customer, use the special endpoint
      let response;
      if (forCustomerEmail) {
        response = await axios.post(`${API}/projects/admin/create-for-customer`, {
          customer_email: forCustomerEmail,
          ...payload
        }, { withCredentials: true });
        toast.success(`Project created for ${forCustomerEmail}! Select a theme.`);
      } else {
        // Regular user creating their own project
        response = await axios.post(`${API}/projects`, payload, { withCredentials: true });
        toast.success('Project created! Select your theme.');
      }
      
      // Redirect to theme selection
      navigate(`/themes/${response.data.project_id}`);
      
    } catch (error) {
      console.error('Error creating project:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        // Redirect to Google OAuth login (custom domain)
        window.location.href = `${API}/auth/google/login?redirect=/briefing`;
      } else if (error.response?.status === 403) {
        toast.error('Admin access required to create projects for customers');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Business Info' },
    { number: 2, title: 'Description' },
    { number: 3, title: 'Content & Logo' },
    { number: 4, title: 'Preferences' }
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title={forCustomerEmail ? `New Project for ${forCustomerEmail}` : 'New Project'} />

      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      data-testid={`step-indicator-${step.number}`}
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                        currentStep === step.number 
                          ? 'bg-amber-500 text-white' 
                          : currentStep > step.number 
                            ? 'bg-amber-100 text-amber-600' 
                            : 'bg-zinc-200 text-zinc-500'
                      }`}
                    >
                      {step.number}
                    </div>
                    <span className="text-sm mt-2 font-medium">{step.title}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded ${
                      currentStep > step.number ? 'bg-amber-500' : 'bg-zinc-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <Card className="bg-white border border-zinc-200 shadow-lg">
            <CardContent className="p-8 bg-white">
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-bold mb-2 text-zinc-900" style={{fontFamily: 'Outfit'}}>Tell us about your business</h2>
                    <p className="text-zinc-600">Let&apos;s start with the basics</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="business_name" className="text-zinc-900">Business Name *</Label>
                      <Input
                        id="business_name"
                        data-testid="business-name-input"
                        placeholder="e.g., Acme Coffee Roasters"
                        value={formData.business_name}
                        onChange={(e) => handleInputChange('business_name', e.target.value)}
                        className="mt-2 bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="business_type" className="text-zinc-900">Business Type *</Label>
                      <Input
                        id="business_type"
                        data-testid="business-type-input"
                        placeholder="e.g., Coffee Shop, Tech Startup, Consulting Firm"
                        value={formData.business_type}
                        onChange={(e) => handleInputChange('business_type', e.target.value)}
                        className="mt-2 bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="target_audience" className="text-zinc-900">Target Audience</Label>
                      <Input
                        id="target_audience"
                        data-testid="target-audience-input"
                        placeholder="e.g., Small business owners, coffee enthusiasts"
                        value={formData.target_audience}
                        onChange={(e) => handleInputChange('target_audience', e.target.value)}
                        className="mt-2 bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-bold mb-2 text-zinc-900" style={{fontFamily: 'Outfit'}}>Describe your business</h2>
                    <p className="text-zinc-600">Help our AI understand what makes you unique</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="business_description" className="text-zinc-900">Business Description *</Label>
                    <Textarea
                      id="business_description"
                      data-testid="business-description-textarea"
                      placeholder="Tell us about your business, what you offer, your mission, and what makes you different..."
                      value={formData.business_description}
                      onChange={(e) => handleInputChange('business_description', e.target.value)}
                      className="mt-2 min-h-[200px] bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    />
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-bold mb-2 text-zinc-900" style={{fontFamily: 'Outfit'}}>Upload your content</h2>
                    <p className="text-zinc-600">Share any documents or logos you have</p>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-900">Content Documents (PDF, DOC)</Label>
                    <div
                      {...getContentRootProps()}
                      data-testid="content-dropzone"
                      className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition bg-white ${
                        isContentDragActive ? 'border-amber-500 bg-amber-50' : 'border-zinc-300 hover:border-emerald-400'
                      }`}
                    >
                      <input {...getContentInputProps()} />
                      <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
                      <p className="text-sm text-zinc-600">
                        {isContentDragActive ? "Drop files here..." : "Drag & drop content files, or click to browse"}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">PDF, DOC, DOCX accepted</p>
                    </div>
                    
                    {formData.content_files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {formData.content_files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                            <span className="text-sm text-zinc-900">{file.filename}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeContentFile(index)}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-zinc-900">Logo (Optional)</Label>
                    <div
                      {...getLogoRootProps()}
                      data-testid="logo-dropzone"
                      className={`mt-2 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition bg-white ${
                        isLogoDragActive ? 'border-amber-500 bg-amber-50' : 'border-zinc-300 hover:border-emerald-400'
                      }`}
                    >
                      <input {...getLogoInputProps()} />
                      <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
                      <p className="text-sm text-zinc-600">
                        {isLogoDragActive ? 'Drop logo here...' : 'Upload your logo'}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">PNG, JPG, SVG (max 5MB)</p>
                    </div>
                    
                    {formData.logo_file && (
                      <div className="mt-4 p-3 bg-zinc-50 rounded-lg flex items-center gap-3">
                        <img 
                          src={`data:image/png;base64,${formData.logo_file.base64_data}`} 
                          alt="Logo preview" 
                          className="w-12 h-12 object-contain rounded"
                        />
                        <span className="text-sm text-zinc-900">{formData.logo_file.filename}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, logo_file: null }))}
                          className="ml-auto"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-zinc-900">Favicon (Optional)</Label>
                    <p className="text-xs text-zinc-500 mb-2">The small icon shown in browser tabs</p>
                    <div
                      {...getFaviconRootProps()}
                      data-testid="favicon-dropzone"
                      className={`mt-2 border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition bg-white ${
                        isFaviconDragActive ? 'border-amber-500 bg-amber-50' : 'border-zinc-300 hover:border-emerald-400'
                      }`}
                    >
                      <input {...getFaviconInputProps()} />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
                      <p className="text-sm text-zinc-600">
                        {isFaviconDragActive ? 'Drop favicon here...' : 'Upload favicon'}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">PNG, ICO, SVG (max 1MB, ideally 32x32 or 64x64)</p>
                    </div>
                    
                    {formData.favicon_file && (
                      <div className="mt-4 p-3 bg-zinc-50 rounded-lg flex items-center gap-3">
                        <img 
                          src={`data:image/png;base64,${formData.favicon_file.base64_data}`} 
                          alt="Favicon preview" 
                          className="w-8 h-8 object-contain"
                        />
                        <span className="text-sm text-zinc-900">{formData.favicon_file.filename}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, favicon_file: null }))}
                          className="ml-auto"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-3xl font-bold mb-2 text-zinc-900" style={{fontFamily: 'Outfit'}}>Additional preferences</h2>
                    <p className="text-zinc-600">Share inspiration and preferences</p>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-900">Competitor/Inspiration URLs</Label>
                    <p className="text-sm text-zinc-500 mb-2">Share websites you like for design inspiration</p>
                    {formData.competitor_urls.map((url, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <Input
                          data-testid={`competitor-url-${index}`}
                          placeholder="https://example.com"
                          value={url}
                          onChange={(e) => handleCompetitorUrlChange(index, e.target.value)}
                          className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                        />
                      </div>
                    ))}
                    <Button
                      data-testid="add-url-btn"
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addCompetitorUrl}
                      className="mt-2 text-zinc-700"
                    >
                      <LinkIcon className="w-4 h-4 mr-2" /> Add Another URL
                    </Button>
                  </div>
                  
                  <div>
                    <Label htmlFor="font_preferences" className="text-zinc-900">Font Preferences (Optional)</Label>
                    <Input
                      id="font_preferences"
                      data-testid="font-preferences-input"
                      placeholder="e.g., Modern sans-serif, or specific fonts like Montserrat"
                      value={formData.font_preferences}
                      onChange={(e) => handleInputChange('font_preferences', e.target.value)}
                      className="mt-2 bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-zinc-200">
                <Button
                  data-testid="prev-step-btn"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="text-zinc-700"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                </Button>
                
                <Button
                  data-testid="next-step-btn"
                  onClick={nextStep}
                  disabled={loading}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {loading ? 'Creating...' : currentStep === 4 ? 'Create Project' : 'Next'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BriefingPage;